import { PLAN_IDS_BY_MODULE } from "../data/subscriptionPlans";
import { isLocal } from "../config/env";

const PRODUCT_ID_ALIASES = {
  regulator: "policyplan",
};

const toLegacyState = (entry = {}) => {
  if (!entry) {
    return { hasAccess: false, status: "none", label: "Not Available", planId: null };
  }
  return {
    hasAccess: Boolean(entry.has_access),
    visible: entry.visible !== false,
    status: entry.has_access ? "active" : "none",
    label: entry.cta_label || (entry.has_access ? "Enter" : "Not Available"),
    destination: entry.destination || null,
    ctaLabel: entry.cta_label || "Open",
    planId: null,
  };
};

const deriveFallbackState = (currentUser = {}) => {
  const subscriptionType = currentUser?.subscription_type;
  const isActive = currentUser?.subscription_status === "active";
  const fleetAccess = Boolean(subscriptionType && PLAN_IDS_BY_MODULE.fleet.has(subscriptionType) && isActive);
  const chargerAccess = Boolean(
    isActive && (
      currentUser?.signup_channel === "southcoast"
      || subscriptionType === "charger-contact"
    )
  );
  const datahubAccess = Boolean(currentUser?.has_datahub_access);
  const policyAccess = Boolean(
    currentUser?.has_regulator_access
    || currentUser?.has_policy_access
    || (isActive && currentUser?.signup_channel === "southcoast" && currentUser?.__policyDomainAllowed === true)
  );

  return {
    efleetplan: {
      hasAccess: fleetAccess,
      visible: true,
      status: fleetAccess ? "active" : "none",
      label: fleetAccess ? "Enter" : "View plans",
      destination: fleetAccess ? "/fleet/dashboard" : "/subscription?module=fleet",
      ctaLabel: fleetAccess ? "Enter" : "View plans",
      planId: subscriptionType || null,
    },
    chargerplan: {
      hasAccess: chargerAccess,
      visible: true,
      status: chargerAccess ? "active" : "none",
      label: chargerAccess ? "Enter" : "Request demo",
      destination: chargerAccess ? "/charger/dashboard" : "/contact",
      ctaLabel: chargerAccess ? "Enter" : "Request demo",
      planId: null,
    },
    gridplan: {
      hasAccess: false,
      visible: true,
      status: "coming_soon",
      label: "Coming soon",
      destination: "/contact",
      ctaLabel: "Coming soon",
      planId: null,
    },
    policyplan: {
      hasAccess: policyAccess,
      visible: true,
      status: policyAccess ? "active" : "none",
      label: policyAccess ? "Enter" : "Request demo",
      destination: policyAccess ? "/regulator/map" : "/contact",
      ctaLabel: policyAccess ? "Enter" : "Request demo",
      planId: null,
    },
    datahub: {
      hasAccess: datahubAccess,
      visible: datahubAccess,
      status: datahubAccess ? "active" : "none",
      label: datahubAccess ? "Enter" : "Not Available",
      destination: datahubAccess ? "/data" : null,
      ctaLabel:       datahubAccess ? "Enter" : "Not available",
      planId: null,
    },
    internalops: {
      hasAccess: Boolean(currentUser?.has_internal_ops_access),
      visible: Boolean(currentUser?.has_internal_ops_access),
      status: currentUser?.has_internal_ops_access ? "active" : "none",
      label: currentUser?.has_internal_ops_access ? "Enter" : "Not Available",
      destination: currentUser?.has_internal_ops_access ? "/internal" : null,
      ctaLabel: currentUser?.has_internal_ops_access ? "Enter" : "Not available",
      planId: null,
    },
  };
};

export const deriveProductState = (currentUser = {}) => {
  if (isLocal && !currentUser?.__devSimulateView) {
    const amptrans = String(currentUser?.email || "").trim().toLowerCase().endsWith("@amptrans.net");
    const devState = {
      hasAccess: true,
      visible: true,
      status: "dev",
      label: "Dev Access",
      destination: "/home",
      ctaLabel: "Enter",
      planId: currentUser?.subscription_type || null,
    };
    return {
      efleetplan: { ...devState, destination: "/fleet/dashboard" },
      chargerplan: { ...devState, destination: "/charger/dashboard" },
      gridplan: { ...devState, destination: "/utility/select" },
      policyplan: { ...devState, destination: "/regulator/map" },
      datahub: { ...devState, destination: "/data" },
      regulator: { ...devState, destination: "/regulator/map" },
      internalops: {
        hasAccess: amptrans,
        visible: amptrans,
        status: amptrans ? "dev" : "none",
        label: amptrans ? "Enter" : "Not Available",
        destination: amptrans ? "/internal" : null,
        ctaLabel: amptrans ? "Enter" : "Not available",
        planId: null,
      },
    };
  }

  const entitlements = currentUser?.entitlements || null;
  if (!entitlements || currentUser?.__devSimulateView) {
    const fallback = deriveFallbackState(currentUser);
    return {
      ...fallback,
      regulator: fallback.policyplan,
    };
  }

  const mapped = {
    efleetplan: toLegacyState(entitlements.efleetplan),
    chargerplan: toLegacyState(entitlements.chargerplan),
    gridplan: toLegacyState(entitlements.gridplan),
    policyplan: toLegacyState(entitlements.policyplan),
    datahub: toLegacyState(entitlements.datahub),
    internalops: toLegacyState(entitlements.internalops),
  };
  mapped.regulator = mapped.policyplan;
  return mapped;
};

export const hasProductAccess = (currentUser, productId) => {
  const state = deriveProductState(currentUser)[PRODUCT_ID_ALIASES[productId] || productId];
  return Boolean(state?.hasAccess);
};

